// When each line plays, across every shot. The one timing source for pictures, playback, the soundtrack and the
// server's export length. Pure.
import type { Episode, Line, Shot } from "../model/episode";
import type { Viseme } from "../model/face";
import { visemeFrames } from "./visemes";

export const FPS = 24;
const GAP = 0.25; // breath after a line before the next
const EXPORT_TAIL = 0.6; // seconds the export holds the last frame

export type Seg = {
  line: Line;
  shot: Shot;
  start: number;
  end: number; // the line finishes
  next: number; // the next line starts (end plus the gap)
  shotStart: number; // the shot's first line starts: a moving picture plays from there
  visemes: Viseme[]; // the speaker's mouth, one per frame
};
export type Timeline = { segs: Seg[]; total: number };

// A line only has length once its audio is ready.
export const lineLength = (l: Line) => (l.status === "ready" ? l.duration : 0);

const visemeCache = new WeakMap<Line, Viseme[]>();
function visemesOf(line: Line): Viseme[] {
  if (line.status !== "ready") return [];
  let v = visemeCache.get(line);
  if (!v) visemeCache.set(line, (v = visemeFrames(line.cues, line.duration, FPS)));
  return v;
}

export function timeline(ep: Episode): Timeline {
  const segs: Seg[] = [];
  let t = 0;
  for (const shot of ep.shots) {
    const shotStart = t;
    for (const line of shot.lines) {
      const d = lineLength(line);
      const next = t + d + (d > 0 ? GAP : 0);
      segs.push({ line, shot, start: t, end: t + d, next, shotStart, visemes: visemesOf(line) });
      t = next;
    }
  }
  return { segs, total: t };
}

// The line playing at time t (lines with no length are skipped).
export function segAt(tl: Timeline, t: number): Seg | null {
  const live = tl.segs.filter(s => s.next > s.start);
  if (!live.length) return null;
  return live.find(s => t >= s.start && t < s.next) ?? (t < live[0]!.start ? live[0]! : live[live.length - 1]!);
}

export const segOf = (tl: Timeline, lineId: string) => tl.segs.find(s => s.line.id === lineId);
export const exportFrameCount = (tl: Timeline) => Math.ceil((tl.total + EXPORT_TAIL) * FPS);
